/* eslint-disable no-nested-ternary */
import settings from '@tenant/settings'
import { useMemo, useState } from 'react'
import { Divider, Grid, LinearProgress, Typography, useTheme } from '@mui/material'
import { PlatformEvent } from 'lib/util'
import { formatFilesize } from 'lib/util/attachment'
import { useMemoRef, useStyles } from '@platform/react/hook'
import Icon, { FileIcon } from 'ui/Element/Icon'
import MuiListTemplateButton from 'ui/Element/Attachment/List/Template/Button'
import OverflowTooltip from 'ui/Element/Text/OverflowTooltip'

const styles = theme => ({
  rowLeft: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '0.75rem',
    width: 'calc(100% - 2rem)',
  },
  rowTitle: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 'calc(100% - 2rem)',
  },
})

/**
 * Gets the type of the file icon based on `types`.
 *
 * @param {Object} item     file to work with
 * @param {Object} types    predefined types
 * @returns {string|"generic"}
 */
const getFileIcon = (item, types) => {
  const extension = item.name.slice(item.name.lastIndexOf('.') + 1)
  const type = Object.keys(types).find(id => types[id].includes(extension))

  return `${type || 'generic'}`
}

/**
 * A generic list item for usage in a {@link FileExplorer} or, more precisely
 * a {@link FileFolder} component.
 *
 * @param {Object} p
 * @param {Object} p.attachment            the attachment to download
 * @param {Object} p.node                  the current node to render.
 * @param {Object} p.events                events passed from parent component
 * @param {boolean} [p.showProgress=false] whether to show the download progress
 * @returns {JSX.Element}
 */
const GenericFileItem = ({
  attachment,
  node,
  events,
  showProgress = false,
  ...options
}) => {
  const theme = useTheme()
  const [progress, setProgress] = useState(0)
  const isLoading = useMemo(() => !!(progress && (progress > 0 && progress < 100)), [progress])

  const { documentationTreeBoldLabels } = settings

  const { classes: parentClasses, header, footer } = options
  const classes = { ...useStyles(styles)(), ...parentClasses }

  const handleClick = () => {
    const clickEvent = new PlatformEvent('click', {
      id: node.id,
      skipSingleNode: options.skipSingleNode,
      notifyOnSkippable: options.notifyOnSkippable,
    })
    events?.onClick(clickEvent)
  }

  const nodeType = events?.onNodeType(node.type)

  const labelEvent = new PlatformEvent('label', { node, list: true })
  const nodeLabel = events?.onNodeLabel?.(labelEvent)

  const fileType = nodeType === 'File' && node?.attachment?.value?.name
    ? getFileIcon(node?.attachment?.value, options.types)
    : node?.url
      ? getFileIcon({ name: node.url }, options.types)
      : null

  const fileIcon = options.fileIcons
  // if node.icon is set, it takes precedence
    ? (node?.icon && <Icon
      source={node.icon}
      // if not, use generic file icon
    />) || (nodeType === 'File' && <FileIcon name={fileType} />)
    : null

  const forwardIcon = (
    <Icon
      source={options.forwardIcon}
      name={'chevron-right'}
      color={'gray.500'}
    />
  )

  return (
    <>
      {isLoading && showProgress ? (
        <LinearProgress
          className={classes.progress}
          variant={'determinate'}
          value={progress}
        />
      ): <></>}
      <Grid
        container
        onClick={handleClick}
        className={classes.folderRow}
      >
        <Grid
          item
          className={classes.rowLeft}
        >
          {options.fileIcons && fileIcon}
          <Grid className={classes.rowTitle}>
            {header && (
              <Typography variant={'12/bold'}>
                {header}
              </Typography>
            )}
            <OverflowTooltip
              component={Typography}
              variant={
                nodeType === 'File' && documentationTreeBoldLabels
                  ? '14/bold'
                  : '14/medium'
              }>
              {nodeLabel || node.name}
            </OverflowTooltip>
            {footer && (
              <Typography
                variant={'12/bold'}
                color={theme.palette.text.link}
              >
                {footer}
              </Typography>
            )}
          </Grid>
          {attachment && isLoading ? (
            <Typography
              variant={'14/medium'}
              sx={{ color: 'text.secondary' }}
            >
              {formatFilesize(attachment.value.size)}
            </Typography>
          ): <></>}
        </Grid>
        {attachment
          ? <Grid
            item
            className={classes.icon}
          >
            <MuiListTemplateButton
              group={options.group}
              attachment={attachment}
              showProgress={false}
              icon={options.downloadIcon}
              value={null}
              {...{ setProgress }}
            />
          </Grid>
          : <Grid
            item
            className={classes.icon}
          >
            {forwardIcon}
          </Grid>
        }
      </Grid>
      {!options.lastItem && (
        <Divider className={classes.divider} />
      )}
    </>
  )
}

export default useMemoRef(GenericFileItem, props => [props.node, props.classes, props.events])
